import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { MessageCircle, MapPin, Download, FileText, Image as ImageIcon, Eye, ClipboardList, Sparkles, Info, RotateCcw, Copy, Check } from "lucide-react";
import { BrandLogo } from "@/components/Brand";
import { trackEvent } from "@/lib/api";
import logo from "@/assets/farma-logo.png";

const CHIP = {
  Lieve: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  Moderato: "bg-[#FEF3C7] text-[#B45309] border-[#FDE68A]",
  Evidente: "bg-[#FFEDD5] text-[#C2410C] border-[#FED7AA]",
  "Non valutabile": "bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]",
};

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <span className="grid place-items-center w-9 h-9 rounded-xl bg-[var(--fs-tint)] text-[var(--fs-blue)]">
      <Icon size={18} />
    </span>
    <h2 className="text-lg font-bold text-[var(--fs-ink)]">{children}</h2>
  </div>
);

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

export default function Report({ report, settings, sessionId, onRestart, preview = false }) {
  const exportRef = useRef(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { observations = [], questionnaire_summary = [], priorities = [], limitations = [] } = report;
  const date = fmtDate(report.date);

  const capture = async () => {
    return html2canvas(exportRef.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
  };

  const saveImage = async () => {
    setBusy(true);
    try {
      const canvas = await capture();
      const link = document.createElement("a");
      link.download = "skin-profile-farma-store.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
      if (!preview) trackEvent(sessionId, "report_saved");
    } finally {
      setBusy(false);
      setSaveOpen(false);
    }
  };

  const savePdf = async () => {
    setBusy(true);
    try {
      const canvas = await capture();
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const w = pw;
      const h = (canvas.height * w) / canvas.width;
      let pos = 0;
      let left = h;
      // paginate if taller than one page
      while (left > 0) {
        pdf.addImage(img, "PNG", 0, pos, w, h);
        left -= ph;
        if (left > 0) {
          pdf.addPage();
          pos -= ph;
        }
      }
      pdf.save("skin-profile-farma-store.pdf");
      if (!preview) trackEvent(sessionId, "report_saved");
    } finally {
      setBusy(false);
      setSaveOpen(false);
    }
  };

  const buildSummaryText = () => {
    const lines = [];
    lines.push(settings.whatsapp_intro || "Ciao Farma Store, ho completato il vostro Skin Analyzer.");
    lines.push("Ecco il mio Skin Profile (report testuale, senza fotografie):");
    if (report.age) lines.push(`Età: ${report.age}`);
    lines.push("");
    lines.push("— COSA AVETE OSSERVATO —");
    if (observations.length === 0) {
      lines.push("Nessuna osservazione attendibile da queste fotografie.");
    } else {
      observations.forEach((o) => lines.push(`• ${o.label}: ${o.severity_it} — ${o.detail}`));
    }
    if (questionnaire_summary.length > 0) {
      lines.push("");
      lines.push("— COSA VI HO RACCONTATO —");
      questionnaire_summary.forEach((b) => lines.push(`• ${b}`));
    }
    if (priorities.length > 0) {
      lines.push("");
      lines.push("— LE MIE PRIORITÀ COSMETICHE —");
      priorities.forEach((p, i) => lines.push(`${i + 1}. ${p.title} — ${p.text}`));
    }
    lines.push("");
    lines.push("Vorrei un consiglio per costruire la mia routine.");
    return lines.join("\n");
  };

  const whatsapp = () => {
    if (!preview) trackEvent(sessionId, "whatsapp_sent");
    // Send ONLY the textual Skin Profile summary — never facial photographs.
    const text = buildSummaryText();
    const url = `https://wa.me/${settings.whatsapp_number}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const directions = () => {
    if (!preview) trackEvent(sessionId, "directions_clicked");
    window.open(settings.maps_url, "_blank");
  };

  const [copied, setCopied] = useState(false);
  const copyProfile = async () => {
    const text = buildSummaryText();
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.warn("Clipboard API unavailable, using fallback", err);
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (e2) { console.warn("Copy fallback failed", e2); }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div className="bg-[var(--fs-page)] min-h-[100dvh]">
      <div className="fs-container py-8">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <span className="fs-eyebrow">Skin Analyzer</span>
          <h1 className="text-3xl font-bold mt-1" data-testid="report-title">Il tuo Skin Profile</h1>
          <p className="mt-2 text-sm text-[var(--fs-ink-2)] leading-relaxed">
            Una lettura cosmetica basata sulle caratteristiche visibili nelle fotografie e sulle informazioni che ci hai fornito.
          </p>
          {date && <p className="mt-1 text-xs text-[var(--fs-muted)]">{date}</p>}
        </motion.div>

        {/* COSA ABBIAMO OSSERVATO */}
        <section className="mt-8" data-testid="observations-section">
          <SectionTitle icon={Eye}>Cosa abbiamo osservato</SectionTitle>
          {observations.length === 0 ? (
            <div className="fs-card p-5 text-sm text-[var(--fs-ink-2)]">
              Da queste fotografie non è stato possibile ricavare osservazioni cosmetiche sufficientemente attendibili.
            </div>
          ) : (
            <div className="space-y-3">
              {observations.map((o, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="fs-card p-4"
                  data-testid={`observation-card-${o.parameter}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-[15px] font-semibold text-[var(--fs-ink)]">{o.label}</h3>
                    <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full border ${CHIP[o.severity_it] || CHIP["Non valutabile"]}`}>
                      {o.severity_it}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm text-[var(--fs-ink-2)] leading-relaxed">{o.detail}</p>
                </motion.div>
              ))}
            </div>
          )}
          {limitations.length > 0 && (
            <div className="mt-3 flex items-start gap-2 text-xs text-[var(--fs-muted)]">
              <Info size={14} className="mt-0.5 shrink-0" />
              <span>{limitations.join(" · ")}</span>
            </div>
          )}
        </section>

        {/* COSA CI HAI RACCONTATO */}
        {questionnaire_summary.length > 0 && (
          <section className="mt-8" data-testid="questionnaire-summary-section">
            <SectionTitle icon={ClipboardList}>Cosa ci hai raccontato</SectionTitle>
            <div className="fs-card p-5">
              <ul className="space-y-2.5">
                {questionnaire_summary.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[var(--fs-ink)]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--fs-blue)] mt-2 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* LE TUE PRIORITÀ */}
        {priorities.length > 0 && (
          <section className="mt-8" data-testid="priorities-section">
            <SectionTitle icon={Sparkles}>Le tue priorità cosmetiche</SectionTitle>
            <div className="space-y-3">
              {priorities.map((p, i) => (
                <div key={i} className="fs-card p-4 flex gap-3.5" data-testid={`priority-card-${i}`}>
                  <span className="shrink-0 w-8 h-8 rounded-full bg-[var(--fs-deep)] text-white grid place-items-center font-bold text-sm">{i + 1}</span>
                  <div>
                    <h3 className="text-[15px] font-bold text-[var(--fs-deep)] uppercase tracking-wide">{p.title}</h3>
                    <p className="mt-1 text-sm text-[var(--fs-ink-2)] leading-relaxed">{p.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* DISCLAIMERS */}
        <div className="mt-8 p-4 rounded-2xl bg-slate-50 border border-[var(--fs-border)] space-y-2" data-testid="report-disclaimer">
          <p className="text-xs text-[var(--fs-muted)] leading-relaxed">{settings.disclaimer_main}</p>
          <p className="text-xs text-[var(--fs-muted)] leading-relaxed">{settings.disclaimer_influence}</p>
          <p className="text-xs text-[var(--fs-muted)] leading-relaxed">{settings.disclaimer_no_physio}</p>
        </div>

        {/* COMMERCIAL CTA */}
        <section className="mt-10 rounded-3xl bg-[var(--fs-deep)] text-white p-6 sm:p-7" data-testid="cta-section">
          <h2 className="text-xl font-bold leading-snug">{settings.cta_headline}</h2>
          <p className="mt-3 text-sm text-blue-100/90 leading-relaxed">{settings.cta_copy}</p>

          <button onClick={() => setSaveOpen(true)} className="mt-6 w-full py-4 rounded-full bg-white text-[var(--fs-deep)] font-semibold flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors fs-focus" data-testid="save-profile-button">
            <Download size={18} /> PORTA IL PROFILO CON TE
          </button>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <button onClick={whatsapp} className="py-3.5 rounded-full bg-white/10 border border-white/25 text-white font-semibold text-sm flex items-center justify-center gap-2 hover:bg-white/20 transition-colors fs-focus" data-testid="whatsapp-button">
              <MessageCircle size={16} /> Invia a Farma Store
            </button>
            <button onClick={directions} className="py-3.5 rounded-full bg-white/10 border border-white/25 text-white font-semibold text-sm flex items-center justify-center gap-2 hover:bg-white/20 transition-colors fs-focus" data-testid="directions-button">
              <MapPin size={16} /> Come raggiungerci
            </button>
          </div>
          <button onClick={copyProfile} className="mt-3 w-full py-3.5 rounded-full bg-white/10 border border-white/25 text-white font-semibold text-sm flex items-center justify-center gap-2 hover:bg-white/20 transition-colors fs-focus" data-testid="copy-profile-button">
            {copied ? <><Check size={16} /> Profilo copiato</> : <><Copy size={16} /> Copia il mio profilo</>}
          </button>
          <p className="mt-3 text-[11px] text-blue-200/70 text-center">L'invio WhatsApp contiene solo il profilo testuale, mai le tue fotografie.</p>
          <p className="mt-4 text-xs text-blue-200/80 text-center">{settings.store_name} · {settings.store_address}</p>
        </section>

        <button onClick={onRestart} className="mt-6 w-full py-3 text-sm text-[var(--fs-ink-2)] flex items-center justify-center gap-2 fs-focus rounded-full" data-testid="report-restart-button">
          <RotateCcw size={15} /> Esegui un nuovo Skin Check
        </button>
      </div>

      {/* Save options sheet */}
      {saveOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4" onClick={() => !busy && setSaveOpen(false)} data-testid="save-sheet">
          <div className="w-full max-w-sm fs-card p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold">Salva il tuo Skin Profile</h3>
            <p className="text-sm text-[var(--fs-muted)] mt-1">Le fotografie del volto non sono incluse nel documento.</p>
            <div className="mt-5 space-y-3">
              <button onClick={savePdf} disabled={busy} className="w-full py-3.5 rounded-2xl border border-[var(--fs-border)] flex items-center gap-3 px-4 hover:bg-[var(--fs-tint)] transition-colors fs-focus disabled:opacity-50" data-testid="save-pdf-button">
                <FileText size={18} className="text-[var(--fs-blue)]" /> <span className="text-sm font-medium">Documento PDF</span>
              </button>
              <button onClick={saveImage} disabled={busy} className="w-full py-3.5 rounded-2xl border border-[var(--fs-border)] flex items-center gap-3 px-4 hover:bg-[var(--fs-tint)] transition-colors fs-focus disabled:opacity-50" data-testid="save-image-button">
                <ImageIcon size={18} className="text-[var(--fs-blue)]" /> <span className="text-sm font-medium">Immagine verticale</span>
              </button>
            </div>
            {busy && <p className="mt-4 text-xs text-center text-[var(--fs-muted)]">Preparazione del file…</p>}
          </div>
        </div>
      )}

      {/* Copy confirmation toast */}
      {copied && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] fs-fade-up" data-testid="copy-toast">
          <div className="flex items-center gap-2 bg-[var(--fs-ink)] text-white text-sm font-medium px-4 py-3 rounded-full shadow-lg">
            <Check size={16} className="text-emerald-400" /> Profilo copiato negli appunti
          </div>
        </div>
      )}

      {/* Off-screen printable layout (no facial photos) */}
      <div style={{ position: "fixed", left: -9999, top: 0 }} aria-hidden>
        <div ref={exportRef} style={{ width: 460, background: "#fff", padding: 28, fontFamily: "'DM Sans', sans-serif", color: "#0F172A" }}>
          <img src={logo} alt="" style={{ height: 34, objectFit: "contain" }} />
          <div style={{ fontSize: 11, letterSpacing: 2, color: "#2563EB", fontWeight: 700, marginTop: 10 }}>SKIN ANALYZER</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>Il tuo Skin Profile</div>
          {date && <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>{date}</div>}

          <div style={{ fontSize: 13, fontWeight: 700, marginTop: 20, color: "#1E3A8A" }}>COSA ABBIAMO OSSERVATO</div>
          {observations.length === 0 && <div style={{ fontSize: 12, color: "#475569", marginTop: 6 }}>Nessuna osservazione attendibile da queste fotografie.</div>}
          {observations.map((o, i) => (
            <div key={i} style={{ border: "1px solid #E2E8F0", borderRadius: 12, padding: 12, marginTop: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{o.label}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#475569" }}>{o.severity_it}</span>
              </div>
              <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>{o.detail}</div>
            </div>
          ))}

          {questionnaire_summary.length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, marginTop: 20, color: "#1E3A8A" }}>COSA CI HAI RACCONTATO</div>
              <ul style={{ margin: "8px 0 0", paddingLeft: 16 }}>
                {questionnaire_summary.map((b, i) => (
                  <li key={i} style={{ fontSize: 12, color: "#0F172A", marginBottom: 4 }}>{b}</li>
                ))}
              </ul>
            </>
          )}

          {priorities.length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, marginTop: 20, color: "#1E3A8A" }}>LE TUE PRIORITÀ COSMETICHE</div>
              {priorities.map((p, i) => (
                <div key={i} style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#1E3A8A" }}>{i + 1}. {p.title}</div>
                  <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>{p.text}</div>
                </div>
              ))}
            </>
          )}

          <div style={{ fontSize: 9, color: "#94A3B8", marginTop: 20, lineHeight: 1.5 }}>{settings.disclaimer_main}</div>
          <div style={{ fontSize: 10, color: "#475569", marginTop: 14, fontWeight: 600 }}>{settings.cta_headline}</div>
          <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 10, borderTop: "1px solid #E2E8F0", paddingTop: 10 }}>{settings.report_footer}</div>
        </div>
      </div>
    </div>
  );
}
